import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, Save, CheckCircle, AlertCircle, Volume2, List, Music, Hash, Bot, ShieldOff, Copy, Check, UserPlus, Download, MessageSquare, Pin, Trash2, Plus, TrendingUp, Mic, Lock, Sparkles, Star } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';
import { toast } from '@/components/ui/sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

function IdResolveBadge({ guildId, kind, value }) {
  const [result, setResult] = useState(null); // null | 'loading' | {resolved, name}
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const v = (value || '').trim();
    if (!v || !guildId) {
      setResult(null);
      return undefined;
    }
    setResult('loading');
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/settings/${guildId}/resolve`, { params: { kind, value: v } });
        setResult(data);
      } catch {
        setResult({ resolved: false });
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [guildId, kind, value]);

  if (!result) return null;
  if (result === 'loading') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 mt-1.5 font-[Manrope]">
        <div className="w-3 h-3 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" />
        Checking...
      </span>
    );
  }
  if (result.resolved) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 mt-1.5 font-[Manrope]" data-testid={`resolve-badge-${kind}`}>
        <CheckCircle size={12} /> {kind === 'role' ? `@${result.name}` : `#${result.name}`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-red-400 mt-1.5 font-[Manrope]" data-testid={`resolve-badge-${kind}`}>
      <AlertCircle size={12} /> No matching {kind} found in this server
    </span>
  );
}

function WelcomeSettingsCard({ guildId }) {
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    welcome_enabled: false, welcome_channel_id: '', welcome_message: '',
    goodbye_enabled: false, goodbye_channel_id: '', goodbye_message: '',
    auto_role_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/announce/channels/${guildId}`),
      axios.get(`${API}/api/reaction-roles/${guildId}/roles`),
      axios.get(`${API}/api/welcome-settings/${guildId}`),
    ]).then(([chRes, roleRes, settingsRes]) => {
      setChannels(chRes.data.channels || []);
      setRoles(roleRes.data.roles || []);
      const s = settingsRes.data.settings || {};
      setForm(f => ({ ...f, ...s }));
    }).catch(console.error).finally(() => setLoading(false));
  }, [guildId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/welcome-settings`, { ...form, guild_id: guildId });
      setSaved(true);
      toast.success('Welcome/Goodbye/Auto-Role settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="welcome-settings-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <UserPlus size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Welcome, Goodbye &amp; Auto-Role</h2>
      </div>

      <div className="space-y-5">
        {/* Welcome */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-zinc-300 font-[Manrope]">Welcome Message</label>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, welcome_enabled: !f.welcome_enabled }))}
              data-testid="welcome-toggle"
              className={`relative w-11 h-6 rounded-full transition-colors ${form.welcome_enabled ? 'bg-[#5865F2]' : 'bg-white/10'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.welcome_enabled ? 'left-6' : 'left-1'}`} />
            </button>
          </div>
          <select
            value={form.welcome_channel_id || ''}
            onChange={e => setForm(f => ({ ...f, welcome_channel_id: e.target.value }))}
            data-testid="welcome-channel-select"
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm mb-2 focus:outline-none focus:border-[#5865F2] transition-colors"
          >
            <option value="">Select a channel...</option>
            {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          <textarea
            value={form.welcome_message || ''}
            onChange={e => setForm(f => ({ ...f, welcome_message: e.target.value }))}
            placeholder="Welcome {user} to {server}! We're now {membercount} members strong."
            rows={2}
            data-testid="welcome-message-input"
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors resize-none"
          />
          <p className="text-xs text-zinc-500 mt-1 font-[Manrope]">Variables: {'{user}'}, {'{username}'}, {'{server}'}, {'{membercount}'}</p>
        </div>

        {/* Goodbye */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-zinc-300 font-[Manrope]">Goodbye Message</label>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, goodbye_enabled: !f.goodbye_enabled }))}
              data-testid="goodbye-toggle"
              className={`relative w-11 h-6 rounded-full transition-colors ${form.goodbye_enabled ? 'bg-[#5865F2]' : 'bg-white/10'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.goodbye_enabled ? 'left-6' : 'left-1'}`} />
            </button>
          </div>
          <select
            value={form.goodbye_channel_id || ''}
            onChange={e => setForm(f => ({ ...f, goodbye_channel_id: e.target.value }))}
            data-testid="goodbye-channel-select"
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm mb-2 focus:outline-none focus:border-[#5865F2] transition-colors"
          >
            <option value="">Select a channel...</option>
            {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          <textarea
            value={form.goodbye_message || ''}
            onChange={e => setForm(f => ({ ...f, goodbye_message: e.target.value }))}
            placeholder="{user} has left {server}. We're now {membercount} members."
            rows={2}
            data-testid="goodbye-message-input"
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors resize-none"
          />
        </div>

        {/* Auto-role */}
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">Auto-Role on Join</label>
          <select
            value={form.auto_role_id || ''}
            onChange={e => setForm(f => ({ ...f, auto_role_id: e.target.value }))}
            data-testid="auto-role-select"
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
          >
            <option value="">None</option>
            {roles.map(r => <option key={r.id} value={r.id}>@{r.name}</option>)}
          </select>
          <p className="text-xs text-zinc-500 mt-2 font-[Manrope]">
            Automatically given to new members when they join. The bot's role must be above this role in Server Settings → Roles.
          </p>
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="welcome-save-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
        >
          {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
        </button>
      </div>
    </motion.div>
  );
}

function ExportDataCard({ guildId }) {
  const [exporting, setExporting] = useState(null);

  const exportCsv = async (kind) => {
    setExporting(kind);
    try {
      const res = await axios.get(`${API}/api/export/activity`, {
        params: { guild_id: guildId, kind },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind}_${guildId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to export data');
    } finally {
      setExporting(null);
    }
  };

  const KINDS = [
    { key: 'music', label: 'Music Plays' },
    { key: 'commands', label: 'Command Usage' },
    { key: 'moderation', label: 'Moderation Actions' },
    { key: 'members', label: 'Member Join/Leave' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="export-data-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Download size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Export Activity Data</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {KINDS.map(k => (
          <button
            key={k.key}
            onClick={() => exportCsv(k.key)}
            disabled={exporting === k.key}
            data-testid={`export-${k.key}-btn`}
            className="flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-[var(--border-md)] bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] disabled:opacity-40 text-sm font-medium text-zinc-200 font-[Manrope] transition-colors"
          >
            {k.label}
            <Download size={14} className="text-zinc-500" />
          </button>
        ))}
      </div>
      <p className="text-xs text-zinc-500 mt-3 font-[Manrope]">Exports up to the last 10,000 records as CSV.</p>
    </motion.div>
  );
}

function CustomCommandsCard({ guildId }) {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ trigger: '', response: '', response_type: 'text', cooldown: 0 });

  const load = () => {
    if (!guildId) return;
    setLoading(true);
    axios.get(`${API}/api/custom-commands/${guildId}`)
      .then(res => setCommands(res.data.commands || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, [guildId]);

  const add = async () => {
    if (!form.trigger.trim() || !form.response.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/custom-commands`, { ...form, guild_id: guildId });
      toast.success('Custom command saved');
      setForm({ trigger: '', response: '', response_type: 'text', cooldown: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save command');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (trigger) => {
    try {
      await axios.delete(`${API}/api/custom-commands/${guildId}/${trigger}`);
      setCommands(c => c.filter(cmd => cmd.trigger !== trigger));
    } catch (e) {
      toast.error('Failed to delete command');
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="custom-commands-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <MessageSquare size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Custom Commands</h2>
      </div>

      <div className="space-y-2 mb-4">
        {commands.length === 0 && <p className="text-sm text-zinc-500 font-[Manrope]">No custom commands yet.</p>}
        {commands.map(cmd => (
          <div key={cmd.trigger} className="flex items-start justify-between gap-3 bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-lg px-3 py-2.5" data-testid={`custom-command-${cmd.trigger}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white font-[Manrope]">!{cmd.trigger}</p>
              <p className="text-xs text-zinc-400 font-[Manrope] truncate">{cmd.response}</p>
              {cmd.cooldown > 0 && <p className="text-xs text-zinc-500 font-[Manrope]">Cooldown: {cmd.cooldown}s</p>}
            </div>
            <button onClick={() => remove(cmd.trigger)} data-testid={`delete-command-${cmd.trigger}`} className="text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-2 border-t border-[var(--border-md)] pt-4">
        <div className="flex gap-2">
          <input
            value={form.trigger}
            onChange={e => setForm(f => ({ ...f, trigger: e.target.value.replace(/\s/g, '') }))}
            placeholder="trigger (e.g. rules)"
            data-testid="command-trigger-input"
            className="flex-1 bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
          />
          <select
            value={form.response_type}
            onChange={e => setForm(f => ({ ...f, response_type: e.target.value }))}
            data-testid="command-type-select"
            className="bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
          >
            <option value="text">Text</option>
            <option value="embed">Embed</option>
          </select>
          <input
            type="number"
            min={0}
            value={form.cooldown}
            onChange={e => setForm(f => ({ ...f, cooldown: parseInt(e.target.value, 10) || 0 }))}
            placeholder="Cooldown (s)"
            data-testid="command-cooldown-input"
            className="w-28 bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
          />
        </div>
        <textarea
          value={form.response}
          onChange={e => setForm(f => ({ ...f, response: e.target.value }))}
          placeholder="Response text. Variables: {user}, {username}, {server}, {membercount}"
          rows={2}
          data-testid="command-response-input"
          className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors resize-none"
        />
        <button
          onClick={add}
          disabled={saving}
          data-testid="add-command-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
        >
          <Plus size={16} /> Add Command
        </button>
        <p className="text-xs text-zinc-500 font-[Manrope]">Members can type !trigger in chat to get the response. Max 25 per server.</p>
      </div>
    </motion.div>
  );
}

function StickyNotesCard({ guildId }) {
  const [channels, setChannels] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ channel_id: '', message: '' });

  const load = () => {
    if (!guildId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/announce/channels/${guildId}`),
      axios.get(`${API}/api/sticky-notes/${guildId}`),
    ]).then(([chRes, noteRes]) => {
      setChannels(chRes.data.channels || []);
      setNotes(noteRes.data.notes || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(load, [guildId]);

  const add = async () => {
    if (!form.channel_id || !form.message.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/sticky-notes`, { ...form, guild_id: guildId });
      toast.success('Sticky note set');
      setForm({ channel_id: '', message: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save sticky note');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (channelId) => {
    try {
      await axios.delete(`${API}/api/sticky-notes/${guildId}/${channelId}`);
      setNotes(n => n.filter(note => note.channel_id !== channelId));
    } catch (e) {
      toast.error('Failed to remove sticky note');
    }
  };

  const channelName = (id) => channels.find(c => c.id === id)?.name || id;

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="sticky-notes-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Pin size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Sticky Notes</h2>
      </div>

      <div className="space-y-2 mb-4">
        {notes.length === 0 && <p className="text-sm text-zinc-500 font-[Manrope]">No sticky notes set.</p>}
        {notes.map(note => (
          <div key={note.channel_id} className="flex items-start justify-between gap-3 bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-lg px-3 py-2.5" data-testid={`sticky-note-${note.channel_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white font-[Manrope]">#{channelName(note.channel_id)}</p>
              <p className="text-xs text-zinc-400 font-[Manrope] truncate">{note.message}</p>
            </div>
            <button onClick={() => remove(note.channel_id)} data-testid={`delete-sticky-${note.channel_id}`} className="text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-2 border-t border-[var(--border-md)] pt-4">
        <select
          value={form.channel_id}
          onChange={e => setForm(f => ({ ...f, channel_id: e.target.value }))}
          data-testid="sticky-channel-select"
          className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
        >
          <option value="">Select a channel...</option>
          {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
        </select>
        <textarea
          value={form.message}
          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
          placeholder="Message to keep at the bottom of the channel"
          rows={2}
          data-testid="sticky-message-input"
          className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors resize-none"
        />
        <button
          onClick={add}
          disabled={saving}
          data-testid="add-sticky-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
        >
          <Plus size={16} /> Set Sticky Note
        </button>
        <p className="text-xs text-zinc-500 font-[Manrope]">The bot reposts this message whenever a new message is sent in the channel, keeping it at the bottom.</p>
      </div>
    </motion.div>
  );
}

function LevelingCard({ guildId }) {
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [levelRoles, setLevelRoles] = useState([]);
  const [announceChannelId, setAnnounceChannelId] = useState('');
  const [newRole, setNewRole] = useState({ level: '', role_id: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!guildId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/announce/channels/${guildId}`),
      axios.get(`${API}/api/reaction-roles/${guildId}/roles`),
      axios.get(`${API}/api/levels/${guildId}/settings`),
      axios.get(`${API}/api/levels/${guildId}/leaderboard`),
    ]).then(([chRes, roleRes, settingsRes, lbRes]) => {
      setChannels(chRes.data.channels || []);
      setRoles(roleRes.data.roles || []);
      setAnnounceChannelId(settingsRes.data.settings?.announce_channel_id || '');
      setLevelRoles(settingsRes.data.roles || []);
      setLeaderboard(lbRes.data.leaderboard || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(load, [guildId]);

  const saveAnnounce = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/levels/settings`, { guild_id: guildId, announce_channel_id: announceChannelId || null });
      toast.success('Level-up settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addLevelRole = async () => {
    if (!newRole.level || !newRole.role_id) return;
    try {
      await axios.post(`${API}/api/levels/roles`, { guild_id: guildId, level: parseInt(newRole.level, 10), role_id: newRole.role_id });
      toast.success('Level role reward added');
      setNewRole({ level: '', role_id: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add level role');
    }
  };

  const removeLevelRole = async (level) => {
    try {
      await axios.delete(`${API}/api/levels/roles/${guildId}/${level}`);
      setLevelRoles(r => r.filter(lr => lr.level !== level));
    } catch (e) {
      toast.error('Failed to remove level role');
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.11 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="leveling-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <TrendingUp size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Leveling &amp; XP</h2>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">Level-Up Announcement Channel</label>
        <div className="flex gap-2">
          <select
            value={announceChannelId}
            onChange={e => setAnnounceChannelId(e.target.value)}
            data-testid="level-announce-channel-select"
            className="flex-1 bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
          >
            <option value="">None (no announcements)</option>
            {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          <button
            onClick={saveAnnounce}
            disabled={saving}
            data-testid="level-announce-save-btn"
            className="px-4 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
          >
            <Save size={16} />
          </button>
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">Role Rewards</label>
        <div className="space-y-2 mb-2">
          {levelRoles.length === 0 && <p className="text-sm text-zinc-500 font-[Manrope]">No role rewards set.</p>}
          {levelRoles.map(lr => {
            const role = roles.find(r => r.id === lr.role_id);
            return (
              <div key={lr.level} className="flex items-center justify-between bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-lg px-3 py-2" data-testid={`level-role-${lr.level}`}>
                <span className="text-sm text-zinc-200 font-[Manrope]">Level {lr.level} → @{role ? role.name : lr.role_id}</span>
                <button onClick={() => removeLevelRole(lr.level)} className="text-zinc-500 hover:text-red-400 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          <input
            type="number"
            min={1}
            value={newRole.level}
            onChange={e => setNewRole(r => ({ ...r, level: e.target.value }))}
            placeholder="Level"
            data-testid="level-role-level-input"
            className="w-24 bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
          />
          <select
            value={newRole.role_id}
            onChange={e => setNewRole(r => ({ ...r, role_id: e.target.value }))}
            data-testid="level-role-select"
            className="flex-1 bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
          >
            <option value="">Select role...</option>
            {roles.map(r => <option key={r.id} value={r.id}>@{r.name}</option>)}
          </select>
          <button
            onClick={addLevelRole}
            data-testid="add-level-role-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] text-sm font-semibold text-white font-[Outfit] transition-colors"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">Leaderboard</label>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-zinc-500 font-[Manrope]">No activity yet.</p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {leaderboard.map((entry, i) => (
              <div key={entry.user_id} className="flex items-center justify-between text-sm font-[Manrope] px-3 py-1.5 rounded-lg bg-[var(--bg-hover)]" data-testid={`leaderboard-row-${i}`}>
                <span className="text-zinc-300">#{i + 1} {entry.username}</span>
                <span className="text-zinc-500">Level {entry.level} • {entry.xp} XP</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function TempVoiceCard({ guildId }) {
  const [channels, setChannels] = useState([]);
  const [triggerChannelId, setTriggerChannelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/music/voice-channels/${guildId}`),
      axios.get(`${API}/api/voice-settings/${guildId}`),
    ]).then(([chRes, settingsRes]) => {
      setChannels(chRes.data.channels || []);
      setTriggerChannelId(settingsRes.data.settings?.trigger_channel_id || '');
    }).catch(console.error).finally(() => setLoading(false));
  }, [guildId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/voice-settings`, { guild_id: guildId, trigger_channel_id: triggerChannelId || null });
      setSaved(true);
      toast.success('Temporary voice channel settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="temp-voice-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Mic size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Temporary Voice Channels</h2>
      </div>
      <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">"Join to Create" Channel</label>
      <select
        value={triggerChannelId}
        onChange={e => setTriggerChannelId(e.target.value)}
        data-testid="temp-voice-trigger-select"
        className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm mb-3 focus:outline-none focus:border-[#5865F2] transition-colors"
      >
        <option value="">Disabled</option>
        {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">
        When a member joins this voice channel, the bot creates a new temporary voice channel for them and moves them into it. The channel is deleted once everyone leaves.
      </p>
      <button
        onClick={save}
        disabled={saving}
        data-testid="temp-voice-save-btn"
        className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
      >
        {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
      </button>
    </motion.div>
  );
}

function StarboardCard({ guildId }) {
  const [form, setForm] = useState({ enabled: false, channel_id: '', threshold: 3 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    axios.get(`${API}/api/starboard/${guildId}`)
      .then(r => setForm({ enabled: r.data.enabled || false, channel_id: r.data.channel_id || '', threshold: r.data.threshold ?? 3 }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guildId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/starboard`, { guild_id: guildId, enabled: form.enabled, channel_id: form.channel_id || null, threshold: parseInt(form.threshold) || 3 });
      setSaved(true);
      toast.success('Starboard settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="starboard-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Star size={18} className="text-amber-400" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Starboard</h2>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-zinc-300 font-[Manrope]">Enable Starboard</p>
            <p className="text-xs text-zinc-500 font-[Manrope]">Repost highly-starred messages to a dedicated channel</p>
          </div>
          <button
            data-testid="starboard-toggle"
            onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.enabled ? 'bg-[#5865F2]' : 'bg-white/10'}`}
          >
            <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${form.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
            <Hash size={14} className="inline mr-1.5 text-zinc-500" />
            Starboard Channel ID
          </label>
          <input
            data-testid="starboard-channel-input"
            type="text"
            placeholder="Paste the channel ID"
            value={form.channel_id}
            onChange={e => setForm(f => ({ ...f, channel_id: e.target.value }))}
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
            <Star size={14} className="inline mr-1.5 text-zinc-500" />
            Star Threshold (1–20)
          </label>
          <input
            data-testid="starboard-threshold-input"
            type="number"
            min="1"
            max="20"
            value={form.threshold}
            onChange={e => setForm(f => ({ ...f, threshold: parseInt(e.target.value) || 3 }))}
            className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
          />
        </div>
      </div>

      <button
        data-testid="save-starboard-btn"
        onClick={save}
        disabled={saving}
        className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
      >
        {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
      </button>
    </motion.div>
  );
}

function DashboardAccessCard({ guildId }) {
  const [roles, setRoles] = useState([]);
  const [allowedRoleIds, setAllowedRoleIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/reaction-roles/${guildId}/roles`),
      axios.get(`${API}/api/dashboard-access/${guildId}`),
    ]).then(([roleRes, accessRes]) => {
      setRoles(roleRes.data.roles || []);
      setAllowedRoleIds(accessRes.data.settings?.allowed_role_ids || []);
    }).catch(e => {
      if (e.response?.status === 403) setForbidden(true);
      else console.error(e);
    }).finally(() => setLoading(false));
  }, [guildId]);

  const toggleRole = (roleId) => {
    setAllowedRoleIds(ids => ids.includes(roleId) ? ids.filter(id => id !== roleId) : [...ids, roleId]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/dashboard-access`, { guild_id: guildId, allowed_role_ids: allowedRoleIds });
      setSaved(true);
      toast.success('Dashboard access settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || forbidden) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="dashboard-access-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Lock size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Dashboard Access</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-4 font-[Manrope]">
        Staff (Manage Server), moderators and the DJ role get the full dashboard. Everyone else gets a limited member view (their rank, coins, birthday, now playing and song requests). Select roles below to give them the full dashboard too.
      </p>
      <div className="space-y-2 mb-4 max-h-56 overflow-y-auto">
        {roles.length === 0 && <p className="text-sm text-zinc-500 font-[Manrope]">No assignable roles found.</p>}
        {roles.map(r => (
          <label key={r.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border-md)] cursor-pointer" data-testid={`access-role-${r.id}`}>
            <input
              type="checkbox"
              checked={allowedRoleIds.includes(r.id)}
              onChange={() => toggleRole(r.id)}
              className="accent-[#5865F2]"
            />
            <span className="text-sm text-zinc-200 font-[Manrope]">@{r.name}</span>
          </label>
        ))}
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="dashboard-access-save-btn"
        className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
      >
        {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
      </button>
    </motion.div>
  );
}

function AIFeaturesCard({ guildId }) {
  const [channels, setChannels] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [logChannelId, setLogChannelId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!guildId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/announce/channels/${guildId}`),
      axios.get(`${API}/api/settings/${guildId}`),
    ]).then(([chRes, settingsRes]) => {
      setChannels(chRes.data.channels || []);
      setEnabled(!!settingsRes.data.ai_moderation_enabled);
      setLogChannelId(settingsRes.data.ai_moderation_log_channel_id || '');
    }).catch(console.error).finally(() => setLoading(false));
  }, [guildId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/${guildId}`, {
        ai_moderation_enabled: enabled,
        ai_moderation_log_channel_id: logChannelId || null,
      });
      setSaved(true);
      toast.success('AI settings saved');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
      data-testid="ai-features-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Sparkles size={18} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">AI Features</h2>
      </div>

      <div className="mb-5">
        <p className="text-sm text-zinc-300 font-[Manrope] mb-1">AI Playlist Generator</p>
        <p className="text-xs text-zinc-500 font-[Manrope]">
          Members can use <span className="text-zinc-300">/aiplaylist</span> to generate and queue a playlist from a vibe or theme (e.g. "chill lo-fi for studying").
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-zinc-300 font-[Manrope]">AI Auto-Moderation</label>
          <button
            type="button"
            onClick={() => setEnabled(v => !v)}
            data-testid="ai-moderation-toggle"
            className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-[#5865F2]' : 'bg-white/10'}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${enabled ? 'left-6' : 'left-1'}`} />
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-2 font-[Manrope]">
          Uses AI to flag messages containing harassment, hate speech, or threats. Flagged messages are logged below and recorded in moderation actions — nothing is auto-deleted.
        </p>
        <select
          value={logChannelId}
          onChange={e => setLogChannelId(e.target.value)}
          data-testid="ai-moderation-log-channel-select"
          className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
        >
          <option value="">No log channel (record only)</option>
          {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
        </select>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="ai-features-save-btn"
        className="flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-40 text-sm font-semibold text-white font-[Outfit] transition-colors"
      >
        {saved ? <><Check size={16} /> Saved!</> : saving ? 'Saving…' : <><Save size={16} /> Save</>}
      </button>
    </motion.div>
  );
}

function AuditLogCard({ guildId }) {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !guildId) return;
    axios.get(`${API}/api/audit-log/${guildId}`, authHeader())
      .then(r => setEntries(r.data.entries || []))
      .catch(() => setEntries([]));
  }, [open, guildId]);

  const actionLabel = (action) => {
    const map = {
      settings_update: 'Updated settings', mod_kick: 'Kicked member', mod_ban: 'Banned member',
      mod_timeout: 'Timed out member', mod_warn: 'Warned member', ban_appeal_approved: 'Approved ban appeal',
      ban_appeal_rejected: 'Rejected ban appeal',
    };
    return map[action] || action;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
      data-testid="audit-log-card"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[var(--bg-hover)] transition-colors"
        data-testid="audit-log-toggle"
      >
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-[#5865F2]" />
          <span className="text-base font-semibold text-white font-[Outfit]">Dashboard Audit Log</span>
        </div>
        <span className="text-zinc-500 text-xs font-[Manrope]">{open ? 'Hide' : 'View'}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 max-h-80 overflow-y-auto space-y-1.5" data-testid="audit-log-list">
          {entries === null ? (
            <p className="text-zinc-500 text-sm text-center py-6">Loading...</p>
          ) : entries.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-6">No dashboard actions logged yet.</p>
          ) : entries.map((e, i) => (
            <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium font-[Manrope]">{actionLabel(e.action)}</p>
                {e.detail && <p className="text-[11px] text-zinc-500 font-[Manrope] truncate">{e.detail}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[11px] text-zinc-400 font-[Manrope]">{e.username}</p>
                <p className="text-[10px] text-zinc-600 font-[Manrope]">{new Date(e.timestamp).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function ConfigTemplatesCard({ guildId }) {
  const [templates, setTemplates] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg] = useState(null);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 3000); };

  const load = () => {
    axios.get(`${API}/api/config-templates`, authHeader())
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
  };
  useEffect(() => { load(); }, []);

  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/config-templates/${guildId}/save`, { name: templateName }, authHeader());
      flash('Template saved!');
      setTemplateName('');
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed', false);
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async (name) => {
    setApplying(name);
    try {
      await axios.post(`${API}/api/config-templates/${encodeURIComponent(name)}/apply/${guildId}`, {}, authHeader());
      flash(`Applied "${name}" — reload to see changes.`);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Apply failed', false);
    } finally {
      setApplying(null);
    }
  };

  const deleteTemplate = async (name) => {
    setDeleting(name);
    try {
      await axios.delete(`${API}/api/config-templates/${encodeURIComponent(name)}`, authHeader());
      setTemplates(prev => prev.filter(t => t.name !== name));
    } catch {
      // ignore
    } finally {
      setDeleting(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
      data-testid="config-templates-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <Copy size={16} className="text-[#5865F2]" />
        <h2 className="text-base font-semibold text-white font-[Outfit]">Config Templates</h2>
      </div>
      <p className="text-xs text-zinc-500 font-[Manrope] mb-3">Save the current server settings as a named template and apply it to any server.</p>

      <div className="flex gap-2 mb-4">
        <input
          data-testid="template-name-input"
          value={templateName}
          onChange={e => setTemplateName(e.target.value)}
          placeholder="Template name"
          className="flex-1 bg-[var(--bg-hover)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#5865F2] font-[Manrope]"
        />
        <button
          onClick={saveTemplate}
          disabled={saving || !templateName.trim()}
          data-testid="save-template-btn"
          className="px-3 py-2 rounded-lg bg-[#5865F2] text-white text-sm font-medium hover:bg-[#4752C4] transition-colors disabled:opacity-50 font-[Manrope]"
        >
          {saving ? '...' : 'Save'}
        </button>
      </div>

      {msg && (
        <p className={`text-xs mb-3 font-[Manrope] ${msg.ok ? 'text-emerald-400' : 'text-red-400'}`}>{msg.text}</p>
      )}

      <div className="space-y-2">
        {templates === null ? (
          <p className="text-zinc-600 text-xs font-[Manrope]">Loading templates...</p>
        ) : templates.length === 0 ? (
          <p className="text-zinc-600 text-xs font-[Manrope]">No saved templates yet.</p>
        ) : templates.map(t => (
          <div key={t.name} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`template-${t.name}`}>
            <span className="flex-1 text-sm text-white font-[Manrope] truncate">{t.name}</span>
            <span className="text-[11px] text-zinc-500 font-[Manrope]">{new Date(t.saved_at).toLocaleDateString()}</span>
            <button
              onClick={() => applyTemplate(t.name)}
              disabled={applying === t.name}
              data-testid={`apply-template-${t.name}`}
              className="text-xs px-2 py-1 rounded bg-[#5865F2]/20 text-[#8B96F0] hover:bg-[#5865F2]/30 transition-colors font-[Manrope] disabled:opacity-50"
            >
              {applying === t.name ? '...' : 'Apply'}
            </button>
            <button
              onClick={() => deleteTemplate(t.name)}
              disabled={deleting === t.name}
              data-testid={`delete-template-${t.name}`}
              className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors font-[Manrope] disabled:opacity-50"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function RoleHierarchyCard({ guildId }) {
  const [roles, setRoles] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || !guildId) return;
    axios.get(`${API}/api/roles/${guildId}/hierarchy`, authHeader())
      .then(r => setRoles(r.data.roles || []))
      .catch(() => setRoles([]));
  }, [open, guildId]);

  const perm_labels = {
    administrator: 'Admin', manage_guild: 'Manage Server', manage_roles: 'Manage Roles',
    manage_channels: 'Manage Channels', ban_members: 'Ban', kick_members: 'Kick',
    moderate_members: 'Timeout', manage_messages: 'Manage Msgs',
    mention_everyone: '@everyone', manage_webhooks: 'Webhooks',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
      data-testid="role-hierarchy-card"
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[var(--bg-hover)] transition-colors"
        data-testid="role-hierarchy-toggle"
      >
        <div className="flex items-center gap-2">
          <Lock size={16} className="text-[#5865F2]" />
          <span className="text-base font-semibold text-white font-[Outfit]">Role Hierarchy</span>
        </div>
        <span className="text-zinc-500 text-xs font-[Manrope]">{open ? 'Hide' : 'View'}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-1.5 max-h-96 overflow-y-auto" data-testid="role-hierarchy-list">
          {roles === null ? (
            <p className="text-zinc-500 text-sm text-center py-6">Loading roles...</p>
          ) : roles.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-6">No roles found.</p>
          ) : roles.map((r, i) => (
            <div
              key={r.id}
              data-testid={`role-row-${r.id}`}
              className="flex items-start gap-3 p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]"
              style={{ marginLeft: `${Math.max(0, (roles.length - 1 - i) * 4)}px`, maxWidth: '100%' }}
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ring-1 ring-white/10"
                style={{ background: r.color || '#4f545c' }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm text-white font-medium font-[Manrope]">{r.name}</span>
                  {r.hoisted && <span className="text-[10px] px-1 py-0.5 rounded bg-indigo-500/15 text-indigo-300">hoisted</span>}
                  {r.managed && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300">bot</span>}
                  <span className="text-[11px] text-zinc-500 ml-auto font-[Manrope]">{r.member_count} members</span>
                </div>
                {r.key_permissions.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {r.key_permissions.map(p => (
                      <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 font-[Manrope]">
                        {perm_labels[p] || p}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default function Settings() {
  const { selectedGuildId } = useGuild();
  const [settings, setSettings] = useState({
    guild_id: selectedGuildId || '',
    default_volume: 50,
    max_queue_size: 100,
    dj_role_id: '',
    autoplay_enabled: false,
    jukebox_mode: false,
    voice_status_enabled: true,
    music_channel_id: '',
    mod_log_channel_id: '',
    announce_channel_id: '',
    warning_expiry_days: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [copiedCmd, setCopiedCmd] = useState(null);

  const copyCommand = (cmd) => {
    navigator.clipboard?.writeText(cmd).catch(() => {});
    setCopiedCmd(cmd);
    setTimeout(() => setCopiedCmd(c => (c === cmd ? null : c)), 1500);
  };

  useEffect(() => {
    if (!selectedGuildId) return;
    setLoading(true);
    axios.get(`${API}/api/settings/${selectedGuildId}`)
      .then(r => setSettings(s => ({ ...s, ...r.data, guild_id: selectedGuildId })))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedGuildId]);

  const saveSettings = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await axios.put(`${API}/api/settings/${selectedGuildId}`, settings);
      setSaveStatus('success');
      toast.success('Settings saved');
    } catch (e) {
      setSaveStatus('error');
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="settings-main">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Outfit] text-white">Settings</h1>
          <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">Configure your Discord bot behavior</p>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* Music Settings */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
            data-testid="music-settings-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <Music size={18} className="text-[#5865F2]" />
              <h2 className="text-base font-semibold text-white font-[Outfit]">Music Settings</h2>
            </div>

            <div className="space-y-5">
              {/* Volume */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                  <Volume2 size={14} className="inline mr-1.5 text-zinc-500" />
                  Default Volume: <span className="text-[#5865F2] font-mono-stats">{settings.default_volume}%</span>
                </label>
                <input
                  data-testid="volume-slider"
                  type="range"
                  min="1"
                  max="100"
                  value={settings.default_volume}
                  onChange={e => setSettings({ ...settings, default_volume: parseInt(e.target.value) })}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #5865F2 ${settings.default_volume}%, rgba(255,255,255,0.1) ${settings.default_volume}%)`
                  }}
                />
                <div className="flex justify-between text-xs text-zinc-600 mt-1">
                  <span>1%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Max Queue Size */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                  <List size={14} className="inline mr-1.5 text-zinc-500" />
                  Max Queue Size
                </label>
                <input
                  data-testid="max-queue-input"
                  type="number"
                  min="1"
                  max="500"
                  value={settings.max_queue_size}
                  onChange={e => setSettings({ ...settings, max_queue_size: parseInt(e.target.value) || 100 })}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white font-mono-stats text-sm focus:outline-none focus:border-[#5865F2] transition-colors"
                />
              </div>

              {/* DJ Role */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                  <Hash size={14} className="inline mr-1.5 text-zinc-500" />
                  DJ Role ID (optional)
                </label>
                <input
                  data-testid="dj-role-input"
                  type="text"
                  placeholder="Leave empty for everyone"
                  value={settings.dj_role_id || ''}
                  onChange={e => setSettings({ ...settings, dj_role_id: e.target.value })}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white font-mono-stats text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
                />
                <IdResolveBadge guildId={selectedGuildId} kind="role" value={settings.dj_role_id} />
              </div>

              {/* Autoplay */}
              <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border-md)] cursor-pointer" data-testid="autoplay-toggle">
                <input
                  type="checkbox"
                  checked={!!settings.autoplay_enabled}
                  onChange={e => setSettings({ ...settings, autoplay_enabled: e.target.checked })}
                  className="accent-[#5865F2]"
                />
                <div>
                  <span className="text-sm text-zinc-200 font-[Manrope]">Autoplay (Radio Mode)</span>
                  <p className="text-xs text-zinc-500 font-[Manrope]">When the queue runs out, keep playing similar songs automatically.</p>
                </div>
              </label>

              {/* Jukebox Mode */}
              <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border-md)] cursor-pointer" data-testid="jukebox-mode-toggle">
                <input
                  type="checkbox"
                  checked={!!settings.jukebox_mode}
                  onChange={e => setSettings({ ...settings, jukebox_mode: e.target.checked })}
                  className="accent-[#5865F2]"
                />
                <div>
                  <span className="text-sm text-zinc-200 font-[Manrope]">Jukebox Mode</span>
                  <p className="text-xs text-zinc-500 font-[Manrope]">Let any member control playback (skip, queue, shuffle, loop) without needing the DJ role.</p>
                </div>
              </label>

              {/* Voice Channel Status */}
              <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border-md)] cursor-pointer" data-testid="voice-status-toggle">
                <input
                  type="checkbox"
                  checked={settings.voice_status_enabled !== false}
                  onChange={e => setSettings({ ...settings, voice_status_enabled: e.target.checked })}
                  className="accent-[#5865F2]"
                />
                <div>
                  <span className="text-sm text-zinc-200 font-[Manrope]">Song in Voice Channel Status</span>
                  <p className="text-xs text-zinc-500 font-[Manrope]">Show the current song as the voice channel status. Requires the Set Voice Channel Status permission.</p>
                </div>
              </label>

              {/* Music Channel */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                  <Hash size={14} className="inline mr-1.5 text-zinc-500" />
                  Music Text Channel ID (optional)
                </label>
                <input
                  data-testid="music-channel-input"
                  type="text"
                  placeholder="Restrict music commands to this channel"
                  value={settings.music_channel_id || ''}
                  onChange={e => setSettings({ ...settings, music_channel_id: e.target.value })}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white font-mono-stats text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
                />
                <IdResolveBadge guildId={selectedGuildId} kind="channel" value={settings.music_channel_id} />
              </div>

              {/* Announcement Channel */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                  <Hash size={14} className="inline mr-1.5 text-zinc-500" />
                  Announcement Channel ID (optional)
                </label>
                <input
                  data-testid="announce-channel-input"
                  type="text"
                  placeholder="Default channel for the dashboard's announcement sender"
                  value={settings.announce_channel_id || ''}
                  onChange={e => setSettings({ ...settings, announce_channel_id: e.target.value })}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white font-mono-stats text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
                />
                <IdResolveBadge guildId={selectedGuildId} kind="channel" value={settings.announce_channel_id} />
              </div>
            </div>

            {/* Save Button */}
            <button
              data-testid="save-settings-btn"
              onClick={saveSettings}
              disabled={saving}
              className="mt-6 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-60 text-white font-semibold font-[Outfit] rounded-xl py-2.5 px-6 flex items-center gap-2 transition-all"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {saving ? 'Saving...' : 'Save Settings'}
            </button>

            {saveStatus === 'success' && (
              <p className="mt-2 text-emerald-400 text-sm flex items-center gap-1" data-testid="save-success">
                <CheckCircle size={14} /> Settings saved!
              </p>
            )}
            {saveStatus === 'error' && (
              <p className="mt-2 text-red-400 text-sm flex items-center gap-1">
                <AlertCircle size={14} /> Failed to save. Try again.
              </p>
            )}
          </motion.div>

          {/* Moderation Settings */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
            data-testid="moderation-settings-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <ShieldOff size={18} className="text-[#5865F2]" />
              <h2 className="text-base font-semibold text-white font-[Outfit]">Moderation Settings</h2>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                Auto-clear warnings after (days)
              </label>
              <input
                data-testid="warning-expiry-input"
                type="number"
                min="0"
                placeholder="Leave empty to never auto-clear"
                value={settings.warning_expiry_days ?? ''}
                onChange={e => setSettings({ ...settings, warning_expiry_days: e.target.value === '' ? null : parseInt(e.target.value) || 0 })}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white font-mono-stats text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
              />
              <p className="text-xs text-zinc-500 mt-2 font-[Manrope]">
                Warnings are automatically marked as cleared this many days after they're issued. New warnings only — existing ones aren't affected. Staff can also clear any warning manually from the Moderation page.
              </p>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-medium text-zinc-300 mb-2 font-[Manrope]">
                <Hash size={14} className="inline mr-1.5 text-zinc-500" />
                Mod Log Channel ID (optional)
              </label>
              <input
                data-testid="mod-log-channel-input"
                type="text"
                placeholder="Bans, kicks, timeouts, warns and purges are posted here"
                value={settings.mod_log_channel_id || ''}
                onChange={e => setSettings({ ...settings, mod_log_channel_id: e.target.value })}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2.5 text-white font-mono-stats text-sm placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors"
              />
              <IdResolveBadge guildId={selectedGuildId} kind="channel" value={settings.mod_log_channel_id} />
            </div>
          </motion.div>

          <AIFeaturesCard guildId={selectedGuildId} />
          <DashboardAccessCard guildId={selectedGuildId} />
          <WelcomeSettingsCard guildId={selectedGuildId} />
          <LevelingCard guildId={selectedGuildId} />
          <TempVoiceCard guildId={selectedGuildId} />
          <StarboardCard guildId={selectedGuildId} />
          <CustomCommandsCard guildId={selectedGuildId} />
          <StickyNotesCard guildId={selectedGuildId} />
          <ExportDataCard guildId={selectedGuildId} />
          <AuditLogCard guildId={selectedGuildId} />
          <ConfigTemplatesCard guildId={selectedGuildId} />
          <RoleHierarchyCard guildId={selectedGuildId} />

          {/* Bot Commands Reference */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6"
            data-testid="commands-reference-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <Bot size={18} className="text-[#5865F2]" />
              <h2 className="text-base font-semibold text-white font-[Outfit]">Bot Commands Reference</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { cmd: '/play [query]', desc: 'Play a song from YouTube' },
                { cmd: '/pause', desc: 'Pause current song' },
                { cmd: '/resume', desc: 'Resume paused song' },
                { cmd: '/skip', desc: 'Skip to next song' },
                { cmd: '/stop', desc: 'Stop and disconnect' },
                { cmd: '/queue', desc: 'Show music queue' },
                { cmd: '/volume [1-100]', desc: 'Set volume' },
                { cmd: '/loop', desc: 'Toggle loop mode' },
                { cmd: '/shuffle', desc: 'Shuffle queue' },
                { cmd: '/nowplaying', desc: 'Show current song' },
                { cmd: '/search [query]', desc: 'Search YouTube' },
                { cmd: '/lyrics [song]', desc: 'Get song lyrics' },
                { cmd: '/playlist save [name]', desc: 'Save current queue' },
                { cmd: '/playlist load [name]', desc: 'Load a playlist' },
                { cmd: '/playlist list', desc: 'List all playlists' },
                { cmd: '/ban @user [reason]', desc: 'Ban a member' },
                { cmd: '/kick @user [reason]', desc: 'Kick a member' },
                { cmd: '/warn @user [reason]', desc: 'Warn a member' },
                { cmd: '/timeout @user [mins]', desc: 'Timeout a member' },
              ].map(({ cmd, desc }) => (
                <button
                  key={cmd}
                  type="button"
                  onClick={() => copyCommand(cmd)}
                  data-testid={`copy-cmd-${cmd.replace(/[^a-z0-9]+/gi, '-')}`}
                  className="relative flex gap-2 p-2.5 rounded-lg bg-[var(--bg-hover)] hover:bg-white/8 text-left transition-colors group"
                >
                  <code className="text-[#5865F2] font-mono-stats text-xs flex-shrink-0">{cmd}</code>
                  <span className="text-zinc-400 text-xs font-[Manrope]">— {desc}</span>
                  <Copy size={12} className="ml-auto text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0 mt-0.5" />
                  <AnimatePresence>
                    {copiedCmd === cmd && (
                      <motion.span
                        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="absolute -top-2 right-2 bg-emerald-500/90 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 font-[Manrope]"
                        data-testid="copied-toast"
                      >
                        <Check size={10} /> Copied!
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              ))}
            </div>
          </motion.div>

        </div>
      </main>
    </div>
  );
}
